// Degraderingstrapp for tale over dårlig samband (kort PTT-13).
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// Gap-analysen la denne trappen under «mesh-native» — men det er en feillesning.
// Trappa gir gevinst ALLEREDE over vanlig svakt 4G, lenge før noe SPARK-mesh
// finnes: full Opus → smalbånds-Opus → ultra-lav bitrate (Codec2/LPCNet) →
// tekst → kun PLI-beacon (posisjon/livstegn). Nivået skal velges AUTOMATISK
// etter link-kvalitet, og — dette er ikke valgfritt — nivået skal være SYNLIG
// for brukeren. En stille degradering (der Opus kveles til et komprimert vrak
// uten at noen sier det) er verre enn en ærlig «du er nå på tekst».
//
// ── HVORFOR RSSI/SNR-MODELLEN FRA meshhealth.ts, IKKE EN NY ─────────────────
// `LinkQuality` (meshhealth.ts) er allerede terskelsatt fra RSSI/SNR (§3: én
// kilde til sannhet). Den er IKKE mesh-spesifikk i typen sin — rssiTier/
// snrTier klassifiserer et radiosignal, uansett om det kommer fra en
// SPARK-mesh-node eller en vanlig 4G-modem-RSSI. Å definere en egen
// «taleLinkKvalitet»-enum ville vært nøyaktig den divergensen §3 forbyr, OG
// ville (utilsiktet) bekreftet gap-analysens feil — at dette er noe eget for
// mesh. Denne fila viser at det ikke er det: samme fem-nivå-skala, samme
// terskler, brukt til et annet formål (kodek-valg, ikke node-helse).
//
// ── PREMISS-SJEKK (ærlig, jf. §1) ────────────────────────────────────────────
// Kortet nevner RSSI/SNR/TAPSRATE. Tapsrate finnes IKKE som et felt noe sted i
// `@skade/contracts` i dag (verifisert mot `MeshSchema` i spark.ts — bare
// rssi_dbm/snr_db/hops/neighbors). Denne fila tar derfor `LinkQuality` (det
// RSSI/SNR allerede gir oss) som eneste reelle input, IKKE en oppdiktet
// tapsrate-parameter. Kobles en ekte tapsrate-kilde inn senere, hører den
// naturlig sammen med `linkQuality()` i meshhealth.ts (samme sted, § om
// verste-vinner), ikke som en ny inngang her.
//
// Kortet nevner heller ingen ekte, koblet kilde for «link-kvalitet på en
// PTT-kanal» i COP i dag (se filhodet i kanaloversikt.ts — samme ærlige gap
// som «aktive sendinger»: ingen LiveKit-klient, ingen RSSI fra GNÅ-telefonen
// strømmer inn i hubben ennå). Funksjonen under er derfor bygget mot en
// EKSPLISITT, DOKUMENTERT inputform (`LinkQuality`) — den later ikke som en
// datakilde finnes bare fordi kontrakten gjør det enkelt å tenke seg en.
import { LINK_QUALITY_LABEL } from './meshhealth.js';
/**
 * Terskeltabell: link-kvalitet → kodek-nivå.
 *
 * ⚠ 1:1 MOT `LinkQuality`, MED VILJE. meshhealth.ts sine RSSI/SNR-terskler
 * (rssiTier/snrTier) er ALLEREDE kalibrert til fem meningsfulle bånd for
 * feltradio-samband — å lage en egen, finere skala her ville vært en ny
 * terskeltabell å holde synkron med den første (§3). Begrunnelsen per nivå:
 *
 *  • utmerket → full-opus:        Nok bånd/SNR til å bære full Opus-kvalitet
 *                                  uten at pakketap begynner å klippe tale.
 *  • god      → smalband-opus:    Bærer Opus, men marginene er tynnere enn
 *                                  «utmerket» — kutt bitraten FØR linken
 *                                  tvinger det, ikke etter en avbrutt samtale.
 *  • marginal → ultra-lav-bitrate: For skjørt for Opus sin pakkestørrelse;
 *                                  Codec2/LPCNet sine få hundre bit/s
 *                                  overlever der Opus ville hakket i stykker.
 *  • darlig   → tekst:             Selv ultra-lav-bitrate-tale krever et
 *                                  kontinuerlig sanntidsstrøm linken ikke
 *                                  holder; tekst tolererer forsinkelse og
 *                                  repeterte sendinger (store-and-forward).
 *  • ingen    → kun-pli-beacon:    Ingen nyttelast er garantert utover en
 *                                  liten, gjentatt posisjons-/livstegn-pakke —
 *                                  MEDEVAC-verdien i «vi vet hvor du er og at
 *                                  du er i live» slår «ingenting kommer gjennom».
 */
const RAA_NIVAA = {
    utmerket: 'full-opus',
    god: 'smalband-opus',
    marginal: 'ultra-lav-bitrate',
    darlig: 'tekst',
    ingen: 'kun-pli-beacon',
};
/** Rangordning best→svakest, som tall (0 = svakest), for hysterese-logikken. */
const NIVAA_RANG = {
    'kun-pli-beacon': 0,
    tekst: 1,
    'ultra-lav-bitrate': 2,
    'smalband-opus': 3,
    'full-opus': 4,
};
/** Menneskelesbar tekst PR. nivå — ALDRI bare et internt navn (kortet krever
 * at nivået er synlig, ikke et tall/kode operatøren må slå opp). */
export const NIVAA_TEKST = {
    'full-opus': 'Full talekvalitet (Opus).',
    'smalband-opus': 'Smalbånds-tale (Opus, redusert bitrate) — noe kvalitetstap, full tale.',
    'ultra-lav-bitrate': 'Ultra-lav bitrate-tale (Codec2/LPCNet) — komprimert/robotisk, men forståelig.',
    tekst: 'Kun tekst — linken bærer ikke sanntids-tale nå. Meldinger går som tekst.',
    'kun-pli-beacon': 'Kun posisjon/livstegn (PLI) — ingen tale eller tekst kommer gjennom nå.',
};
/** Terskel for oppgradering: antall PÅFØLGENDE målinger av et BEDRE nivå som
 * kreves før vi faktisk hever kodek-nivået (se hysterese-begrunnelse under). */
export const OPPGRADERINGS_TERSKEL = 3;
/**
 * Kjernefunksjonen: gitt en link-kvalitet-måling (og forrige tilstand),
 * returner det AKTIVE degraderingsnivået for tale, med tekst og ny tilstand.
 *
 * ⚠ ASYMMETRISK HYSTERESE — DEGRADER RASKT, GJENOPPRETT TREGT. Samme doktrine
 * som fail-degraded-mønsteret i qos.ts («under metning forkastes lavest
 * presedens», ikke fail-open) og verste-vinner i meshhealth.ts:
 *
 *  • Et FALL i kvalitet slår gjennom UMIDDELBART, uansett hvor mange nivåer.
 *    Å fortsette å sende full Opus over en link som akkurat kollapset kaster
 *    bort de siste bitene av båndbredde på en samtale som likevel dør —
 *    bedre å hoppe rett til det nivået som faktisk kommer gjennom.
 *  • En STIGNING i kvalitet krever `terskel` (default `OPPGRADERINGS_TERSKEL`
 *    = 3) PÅFØLGENDE målinger av samme, bedre nivå før vi faktisk hever.
 *    Uten dette ville en link som vipper rundt en terskel (rapportert RSSI
 *    som svinger ±1 dB over «marginal/god»-grensa) flippet kodek-nivå om og
 *    om igjen — hver flipp er en Opus-renegotiation/re-sync brukeren HØRER
 *    som et hakk, verre enn å bli sittende ett nivå lavere en liten stund til.
 *
 * Ustabilitets-telleren nullstilles så snart en måling IKKE bekrefter samme
 * bedre nivå — en enkelt god måling omgitt av dårlige er ikke en trend.
 */
export function taleDegraderingsnivaa(kvalitet, forrige, terskel = OPPGRADERINGS_TERSKEL) {
    const t = Math.max(1, Math.floor(terskel));
    const raa = RAA_NIVAA[kvalitet];
    // Første måling: ingen historie å holde tilbake mot — sett direkte.
    if (!forrige) {
        return ferdig(raa, { nivaa: raa, oppgraderingsTeller: 0, oppgraderingMot: null }, true);
    }
    const gjeldendeRang = NIVAA_RANG[forrige.nivaa];
    const raaRang = NIVAA_RANG[raa];
    if (raaRang <= gjeldendeRang) {
        // Samme nivå, eller verre: bytt UMIDDELBART (fail-degraded, ikke fail-open).
        return ferdig(raa, { nivaa: raa, oppgraderingsTeller: 0, oppgraderingMot: null }, raa !== forrige.nivaa);
    }
    // raaRang > gjeldendeRang: linken ser bedre ut enn nivået vi faktisk kjører.
    // Fortsetter trenden mot samme mål øker telleren; et NYTT mål (skifte av
    // hvilket bedre nivå målingene peker mot) starter telleren på 1 — også når
    // terskelen er 1, slik at en terskel-på-1-konfigurasjon hever med én gang.
    const teller = forrige.oppgraderingMot === raa ? forrige.oppgraderingsTeller + 1 : 1;
    if (teller >= t) {
        return ferdig(raa, { nivaa: raa, oppgraderingsTeller: 0, oppgraderingMot: null }, true);
    }
    return ferdig(forrige.nivaa, { nivaa: forrige.nivaa, oppgraderingsTeller: teller, oppgraderingMot: raa }, false);
}
function ferdig(nivaa, tilstand, endret) {
    return { tilstand, nivaa, tekst: NIVAA_TEKST[nivaa], endret };
}
/**
 * Kort linje til statuslinje/badge: «Smalbånds-tale (God link)». Kobler
 * kodek-nivået til LINK-teksten operatøren allerede kjenner fra mesh-panelet
 * (samme ord, ett sted å lære — jf. filhodet).
 */
export function taleDegraderingsLinje(nivaa, kvalitet) {
    return `${NIVAA_TEKST[nivaa]} (${LINK_QUALITY_LABEL[kvalitet]} link)`;
}
//# sourceMappingURL=taledegradering.js.map